import { fetchPromoPrice } from '@theme/promo-price-lookup';
import { drawPromo, hasPromo, onPromoDrawn, promoFromResult } from '@theme/promo-price-overlay';
import { runSoon, runWhenIdle } from '@theme/promo-schedule';

/**
 * Shows the theme's resolved promo on Bloomreach Loomi cards EARLY (SM-780).
 *
 * The heavy card fragment (templates/product.bloomreach-card.liquid) already
 * carries the promo and assets/promo-price-overlay.js draws it, but that fetch
 * starts only when a card is 300px from the viewport and renders sizes,
 * colourways and quick add first. This module watches for cards as the widget
 * draws them and asks the light `?view=promo` endpoint (templates/product.promo.liquid)
 * through assets/promo-price-lookup.js as soon as a card is within 1200px, so
 * the promo is usually there by the time the card is seen.
 *
 * It is only loaded on a store with promo rules (snippets/promo-early-script.liquid),
 * so a store without any makes no request, downloads nothing and hides nothing.
 *
 * - Cards: `br-product-card` widget hosts have an open shadow root holding the
 *   `<bloomreach-product-card data-product-url>`. One MutationObserver on the
 *   body finds hosts, one per host's shadow root finds cards, including cards the
 *   widget draws again later (filters, sort, load more).
 * - Requests: one per distinct product path, never retried; at most CONCURRENCY
 *   at a time and CEILING per page. A card the widget draws again whose path is
 *   already answered is drawn at once, before it is first painted. A failed or
 *   empty answer leaves the card at the feed price, and the heavy fragment
 *   remains the fallback.
 * - Drawing goes through promo-price-overlay's `drawPromo`: this module never
 *   overwrites a card that already shows a promo, and the fragment's later
 *   promo replaces this one only when it differs.
 *
 * No flash of the feed price. The card is in a shadow root, so theme CSS cannot
 * reach it; the price block is hidden from here, with an inline
 * `visibility: hidden` that keeps its box (no layout shift), only while an
 * answer is on its way, and the previous inline value is put back exactly.
 * A card is revealed when ANY of these happens: its promo is drawn (by this
 * module or by the heavy fragment: the overlay tells us), its answer is empty or
 * failed, REVEAL_TIMEOUT_MS passes, or this module stops. Reveal is idempotent
 * and always runs in a finally. A card is never hidden if it is waiting for a
 * request slot, past the CEILING, outside the early margin, or already showing a
 * promo; and a card already painted on screen when it is found is not hidden
 * either, because hiding it would add a flicker instead of removing one.
 *
 * Requirement left out on purpose: the heavy fragment "finishing" without a
 * promo does not reveal a card. The only signal is `aria-busy` leaving the
 * card's badges, which happens BEFORE the overlay's dynamic import draws a
 * fragment promo, so using it would reveal the feed price and then swap. The
 * timeout covers that rare case.
 *
 * Known edge (accepted): if a rule ends between the light and the heavy request
 * the fragment carries no promo, nothing redraws, and the earlier promo stays
 * until the page is reloaded.
 */

export const EARLY_MARGIN_PX = 1200;
export const EARLY_MARGIN = `${EARLY_MARGIN_PX}px 0px`;
export const CONCURRENCY = 6;
export const CEILING = 60;
export const REVEAL_TIMEOUT_MS = 700;

const PRODUCT_PATH = /\/products\/[\w-]+$/;
const PRICE_BLOCK = '.ceres-card__price';

/**
 * The product path a card links to, or null for anything that is not a
 * same-origin product URL.
 *
 * @param {Element} card - A `bloomreach-product-card`.
 * @returns {string | null}
 */
function productPath(card) {
  try {
    const { origin } = globalThis.location;
    const url = new URL(card.getAttribute('data-product-url') ?? '', origin);
    return url.origin === origin && PRODUCT_PATH.test(url.pathname) ? url.pathname : null;
  } catch {
    return null;
  }
}

/**
 * Starts watching for cards.
 *
 * @param {object} [env] - Injected for tests; the page uses the defaults.
 * @param {typeof fetchPromoPrice} [env.fetchPromo]
 * @param {typeof MutationObserver} [env.MutationObserver]
 * @param {typeof IntersectionObserver | undefined} [env.IntersectionObserver]
 * @param {(callback: () => void) => void} [env.schedule] - Defers a scan to the next frame.
 * @param {(callback: () => void, ms: number) => unknown} [env.setTimer]
 * @param {(timer: unknown) => void} [env.clearTimer]
 * @param {() => number} [env.viewportHeight]
 * @param {Document} [env.document]
 * @returns {{scan: () => void, stop: () => void, requested: () => number}}
 */
export function watchPromoCards(env = {}) {
  const fetchPromo = env.fetchPromo ?? fetchPromoPrice;
  const doc = env.document ?? document;
  // Each browser feature is used only if it exists; without it the work below runs directly, once.
  const Mutations = env.MutationObserver ?? globalThis.MutationObserver;
  const Intersections = 'IntersectionObserver' in env ? env.IntersectionObserver : globalThis.IntersectionObserver;
  const schedule = env.schedule ?? ((callback) => runSoon(callback));
  const setTimer = env.setTimer ?? ((callback, ms) => setTimeout(callback, ms));
  const clearTimer = env.clearTimer ?? ((timer) => clearTimeout(/** @type {any} */ (timer)));
  const viewportHeight = env.viewportHeight ?? (() => window.innerHeight);

  /** @type {WeakSet<object>} */
  const hosts = new WeakSet();
  /** @type {WeakSet<object>} */
  const cards = new WeakSet();
  /** @type {Map<string, Promise<import('@theme/promo-price-lookup').PromoResult | null>>} The answer per path; never evicted, so nothing is asked twice. */
  const answers = new Map();
  /** @type {Map<string, import('@theme/promo-price-lookup').PromoResult | null>} Answers that have arrived, so a card drawn again is drawn without waiting. */
  const settled = new Map();
  /** @type {Map<Element, {block: HTMLElement, value: string, priority: string, timer: unknown}>} Cards whose price is hidden right now. */
  const hiding = new Map();
  /** @type {{path: string, card: Element}[]} Cards waiting for a request slot. They are not hidden. */
  const queue = [];
  let inFlight = 0;
  let scanQueued = false;

  // ------------------------------------------------------------- hide / reveal

  /**
   * Hides the price block of a card whose answer is on its way. Saves the
   * block's own inline visibility first, and arms the timeout before hiding,
   * so a price is never left hidden.
   *
   * @param {Element} card
   */
  function hide(card) {
    if (hiding.has(card)) return;
    try {
      const block = /** @type {HTMLElement | null} */ (card.querySelector(PRICE_BLOCK));
      if (!block) return;

      const { style } = block;
      const state = { block, value: style.getPropertyValue('visibility'), priority: style.getPropertyPriority('visibility'), timer: undefined };
      hiding.set(card, state);
      state.timer = setTimer(() => reveal(card), REVEAL_TIMEOUT_MS);
      style.setProperty('visibility', 'hidden');
    } catch {
      reveal(card);
    }
  }

  /**
   * Puts the price block back exactly as it was. Safe to call any number of
   * times, on any card.
   *
   * @param {object} card
   */
  function reveal(card) {
    const state = hiding.get(/** @type {Element} */ (card));
    if (!state) return;
    hiding.delete(/** @type {Element} */ (card));
    try {
      clearTimer(state.timer);
    } finally {
      const { style } = state.block;
      if (state.value) style.setProperty('visibility', state.value, state.priority);
      else style.removeProperty('visibility');
    }
  }

  // Any draw on any card (this module's, or the heavy fragment's) reveals it.
  const unsubscribe = onPromoDrawn(reveal);

  // ------------------------------------------------------------------ geometry

  /** @param {Element} card */
  function rectOf(card) {
    const rect = card.getBoundingClientRect?.();
    return rect && (rect.width > 0 || rect.height > 0) ? rect : null;
  }

  /** Within the early margin of the viewport, vertically. @param {Element} card */
  function isNear(card) {
    const rect = rectOf(card);
    return Boolean(rect) && /** @type {DOMRect} */ (rect).bottom > -EARLY_MARGIN_PX && /** @type {DOMRect} */ (rect).top < viewportHeight() + EARLY_MARGIN_PX;
  }

  /** Inside the viewport itself, so it may already be painted. @param {Element} card */
  function isOnScreen(card) {
    const rect = rectOf(card);
    return Boolean(rect) && /** @type {DOMRect} */ (rect).bottom > 0 && /** @type {DOMRect} */ (rect).top < viewportHeight();
  }

  /** How far a card is from the viewport; 0 when it is in it. @param {Element} card */
  function distance(card) {
    const rect = rectOf(card);
    if (!rect) return 0;
    if (rect.bottom <= 0) return -rect.bottom;
    return rect.top > viewportHeight() ? rect.top - viewportHeight() : 0;
  }

  // ------------------------------------------------------------------ drawing

  /**
   * @param {Element} card
   * @param {import('@theme/promo-price-lookup').PromoResult | null} result
   */
  function draw(card, result) {
    if (!result || card.isConnected === false) return;
    const promo = promoFromResult(result);
    if (promo) drawPromo(/** @type {any} */ (card), promo, 'early');
  }

  /**
   * Draws what arrived, then reveals the card whatever happened.
   *
   * @param {Element} card
   * @param {import('@theme/promo-price-lookup').PromoResult | null} result
   */
  function finish(card, result) {
    try {
      draw(card, result);
    } catch {
      // The feed price stays; the card is revealed below.
    } finally {
      reveal(card);
    }
  }

  // ------------------------------------------------------------------ requests

  /**
   * Asks for a path now. `hideIt` is only true for a card whose request starts
   * this moment; a card that waited in the queue is never hidden.
   *
   * @param {Element} card
   * @param {string} path
   * @param {boolean} hideIt
   */
  function launch(card, path, hideIt) {
    inFlight += 1;
    if (hideIt) hide(card);
    const answer = new Promise((resolve) => resolve(fetchPromo(path))).catch(() => null);
    answers.set(path, answer);
    void answer.then((result) => {
      inFlight -= 1;
      settled.set(path, result);
      try {
        finish(card, result);
      } finally {
        pump();
      }
    });
  }

  /**
   * A card that needs an answer. Draws a known answer at once; waits on a
   * request already in flight; starts a request if a slot is free; otherwise
   * queues, unhidden.
   *
   * @param {Element} card
   * @param {string} path
   * @param {boolean} mayHide - Whether this card may be hidden while it waits.
   */
  function start(card, path, mayHide) {
    if (hasPromo(card)) return;

    if (settled.has(path)) {
      finish(card, /** @type {any} */ (settled.get(path)));
      return;
    }

    const pending = answers.get(path);
    if (pending) {
      if (mayHide) hide(card);
      void pending.then((result) => finish(card, result));
      return;
    }

    if (answers.size >= CEILING) return;

    if (inFlight < CONCURRENCY) {
      launch(card, path, mayHide);
      return;
    }

    queue.push({ path, card });
  }

  function pump() {
    // Queued cards whose product has been answered, or is being answered, need no slot.
    for (let index = 0; index < queue.length; ) {
      const { path, card } = /** @type {{path: string, card: Element}} */ (queue[index]);
      const answer = answers.get(path);
      if (!answer) {
        index += 1;
        continue;
      }
      queue.splice(index, 1);
      void answer.then((result) => finish(card, result));
    }

    while (queue.length && inFlight < CONCURRENCY) {
      // The card nearest the viewport goes first, so what is on screen gets the slots.
      let nearest = 0;
      for (let index = 1; index < queue.length; index += 1) {
        if (distance(/** @type {any} */ (queue[index]).card) < distance(/** @type {any} */ (queue[nearest]).card)) nearest = index;
      }
      const [{ path, card }] = /** @type {{path: string, card: Element}[]} */ (queue.splice(nearest, 1));
      if (answers.size >= CEILING) continue;
      launch(card, path, false);
    }
  }

  /** @type {IntersectionObserver | null} */
  let intersections = null;
  if (typeof Intersections === 'function') {
    try {
      intersections = new Intersections(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            intersections?.unobserve(entry.target);
            const path = productPath(entry.target);
            // Near, but not yet on screen: hiding costs nothing. A card already on screen is not hidden.
            if (path) start(entry.target, path, !isOnScreen(entry.target));
          }
        },
        { rootMargin: EARLY_MARGIN }
      );
    } catch {
      // A browser that throws here is treated as having no observer: every card is asked at once.
      intersections = null;
    }
  }

  // ------------------------------------------------------------------ finding cards

  /**
   * @param {Element} card
   * @param {boolean} fresh - Found as the widget drew it (not yet painted), not already on the page when this module started.
   */
  function track(card, fresh) {
    if (cards.has(card)) return;
    cards.add(card);

    const path = productPath(card);
    if (!path) return;

    // Before first paint, so a card the widget draws again can have its promo with no flash at all.
    if (settled.has(path)) {
      start(card, path, false);
      return;
    }

    if (!intersections) {
      start(card, path, false);
      return;
    }

    if (isNear(card)) {
      start(card, path, fresh || !isOnScreen(card));
      return;
    }

    intersections.observe(card);
  }

  /**
   * @param {ParentNode} scope
   * @param {boolean} fresh
   */
  function trackWithin(scope, fresh) {
    for (const card of scope.querySelectorAll('bloomreach-product-card')) track(card, fresh);
  }

  const rootObserver = typeof Mutations !== 'function' ? null : new Mutations((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (/** @type {Element} */ (node).matches('bloomreach-product-card')) track(/** @type {Element} */ (node), true);
        else trackWithin(/** @type {Element} */ (node), true);
      }
    }
  });

  /** @param {boolean} fresh */
  function scanHosts(fresh) {
    scanQueued = false;
    for (const host of doc.querySelectorAll('br-product-card')) {
      const root = host.shadowRoot;
      if (!root || hosts.has(host)) continue;
      hosts.add(host);
      rootObserver?.observe(root, { childList: true, subtree: true });
      trackWithin(root, fresh);
    }
  }

  const bodyObserver = typeof Mutations !== 'function' ? null : new Mutations(() => {
    if (scanQueued) return;
    scanQueued = true;
    schedule(() => scanHosts(true));
  });
  bodyObserver?.observe(doc.body, { childList: true, subtree: true });
  // What is already on the page may be painted: found as not fresh.
  scanHosts(false);

  return {
    scan: () => scanHosts(true),
    requested: () => answers.size,
    stop() {
      bodyObserver?.disconnect();
      rootObserver?.disconnect();
      intersections?.disconnect();
      unsubscribe();
      for (const card of [...hiding.keys()]) reveal(card);
    },
  };
}

if (typeof document !== 'undefined' && document.body) {
  const watcher = watchPromoCards();
  // A catch-up scan when the browser is idle (and in any case a few seconds in): cards the observers
  // missed, or that were drawn while the tab was hidden, still get their promo. Cards already
  // tracked are skipped, so with every feature present it finds nothing new.
  runWhenIdle(() => watcher.scan());
}
